import { useQuery } from '@tanstack/react-query';
import type { QuickRecallCard } from '@libertify/ui';
import type {
  Flashcards,
  MediaAsset,
  MediaProjectResponse,
} from '../../types';
import {
  assetUrl,
  projectUrl,
  readJson,
  resolvePresentAddons,
} from '../addOnAssets';
import type {
  QuickRecallAsset,
  QuickRecallDeck,
  UseQuickRecallArgs,
} from './types';


/** Map the raw `Flashcards` asset to the `<QuickRecall>` card shape. */
export const parseQuickRecall = (
  flashcards: Flashcards | null,
): QuickRecallCard[] =>
  (flashcards?.cards ?? []).map((c) => ({ front: c.front, back: c.back }));

/** One key for the deck read and the save's invalidation. */
export const quickRecallKey = (apiBaseUrl: string, projectId: string) =>
  ['quick-recall', apiBaseUrl, projectId] as const;

/**
 * The Quick recall read chain — the same three steps `useExperienceAddons`
 * runs for every add-on, for this one only:
 *
 *   project → storyboards[].add_ons[] → the Flashcards asset_id
 *   asset   → signed path
 *   path    → `Flashcards { cards: [{ front, back }] }`
 *
 * Strict where the viewer is lenient: a deck that can't be read rejects
 * instead of reading as empty, so an editor never offers an empty deck to be
 * saved over the real one.
 */
export const loadQuickRecall = async ({
  apiBaseUrl,
  projectId,
  token,
}: UseQuickRecallArgs): Promise<QuickRecallDeck> => {
  const project = await readJson<MediaProjectResponse>(
    projectUrl(apiBaseUrl, projectId),
    token,
  );
  const assetId = resolvePresentAddons(project).get('Flashcards')?.asset_id;
  if (!assetId) return { cards: [] };
  const asset = await readJson<MediaAsset>(
    assetUrl(apiBaseUrl, assetId),
    token,
  );
  const deck = await readJson<Flashcards>(asset.path);
  return { cards: parseQuickRecall(deck), assetId };
};

/**
 * A project's Quick recall deck on its own. Where `useExperienceAddons` loads
 * every add-on for the viewer, this reads just this one — for callers like
 * the experience edit page that want a single add-on's content, cached by
 * React Query across remounts. Needs a `QueryClientProvider` above it.
 */
export function useQuickRecall({
  apiBaseUrl,
  projectId,
  token,
}: UseQuickRecallArgs): QuickRecallAsset {
  const query = useQuery({
    queryKey: quickRecallKey(apiBaseUrl, projectId),
    queryFn: () => loadQuickRecall({ apiBaseUrl, projectId, token }),
    enabled: Boolean(apiBaseUrl && projectId && token),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    cards: query.data?.cards ?? [],
    assetId: query.data?.assetId,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
